import React from 'react';
import {
  UserIcon,
  UsersIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CogIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  KeyIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const UsersTable = ({
  searchTerm,
  setSearchTerm,
  sortedUsers,
  sortKey,
  sortDir,
  handleSort,
  setDetailsUserId,
  changeUserEmail,
  resetUserPassword,
  removeUser,
  user,
  updateUserMutation,
}) => {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Users</h2>
          <p className="mt-1 text-sm text-gray-600">Manage user accounts and permissions</p>
        </div>
        <div className="relative w-full sm:w-auto">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-80"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="mobile-table min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('id')}
                >
                  <UserIcon className="h-4 w-4" />
                  ID {sortKey === 'id' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('name')}
                >
                  <UsersIcon className="h-4 w-4" />
                  Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('email')}
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  Email {sortKey === 'email' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('role')}
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  Role {sortKey === 'role' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('active')}
                >
                  <CogIcon className="h-4 w-4" />
                  Status {sortKey === 'active' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <button
                  className="flex items-center gap-1 transition-colors hover:text-gray-700"
                  onClick={() => handleSort('last_login')}
                >
                  <CalendarIcon className="h-4 w-4" />
                  Last login{' '}
                  {sortKey === 'last_login' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-1">
                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                  Actions
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedUsers.map((u) => (
              <tr key={u.id}>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900" data-label="ID">
                  {u.id}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900" data-label="Name">
                  {u.first_name} {u.last_name}
                </td>
                <td
                  className="whitespace-nowrap break-words px-4 py-2 text-sm text-gray-900"
                  data-label="Email"
                >
                  {u.email}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900" data-label="Role">
                  {u.is_admin ? 'admin' : 'rower'}
                </td>
                <td
                  className="whitespace-nowrap px-4 py-2 text-sm text-gray-900"
                  data-label="Status"
                >
                  <button
                    className={`rounded px-2 py-1 text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
                    onClick={() =>
                      updateUserMutation.mutate({ id: u.id, data: { is_active: !u.is_active } })
                    }
                  >
                    {u.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td
                  className="whitespace-nowrap px-4 py-2 text-sm text-gray-700"
                  data-label="Last login"
                >
                  {u.last_login
                    ? new Date(u.last_login).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                    : 'Never'}
                </td>
                <td
                  className="whitespace-nowrap px-4 py-3 text-sm text-gray-900"
                  data-label="Actions"
                >
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                      onClick={() => setDetailsUserId(u.id)}
                      title="View user details"
                    >
                      <EyeIcon className="h-3 w-3" />
                      Details
                    </button>
                    <button
                      className="flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      onClick={() => changeUserEmail(u)}
                      title="Change user email"
                    >
                      <EnvelopeIcon className="h-3 w-3" />
                      Email
                    </button>
                    <button
                      className="flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                      onClick={() => resetUserPassword(u)}
                      title="Reset user password"
                    >
                      <KeyIcon className="h-3 w-3" />
                      Password
                    </button>
                    {u.id !== user.id && (
                      <button
                        className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        onClick={() => removeUser(u)}
                        title="Delete user"
                      >
                        <TrashIcon className="h-3 w-3" />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default UsersTable;
